import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Container, Row, Col, Button, InputGroup, Form } from 'react-bootstrap';
import AddCircle from 'mdi-react/AddCircleIcon';
import HighlightOff from 'mdi-react/HighlightOffIcon';
import Search from 'mdi-react/SearchIcon';

import { DefaultRecordType } from 'rc-table/lib/interface';
import { StatusCell } from 'modules/shared/DataGrid/components/Cells';
import { logoutSelector } from 'modules/common/LogoutBtn/selectors';
import ErrorMsg from 'modules/shared/ErrorMsg';
import { LogicTypes, FilterOperators, DataGrid, DataGridColumn } from 'modules/shared/DataGrid';
import { useGridState } from 'modules/shared/DataGrid/hooks/useGridState';
import { Filter, GridFilter } from 'modules/shared/DataGrid/utils/interfaces';
import { RootState } from 'rootReducer';
import TripColumnsMultiSelect from 'modules/shared/TripColumnsMultiSelect';
import { TripStatusIds, tripStatusBackgroundColor, clientDateFormat } from '../../../../entities/Trip';
import { getTripsRequest, getUnloadingTripsRequest, getTripsError } from './services/actions';
import {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  AWAITING_REGISTER_STATUS_ID,
  GetTripColumnsNames,
  MIN_FILTER_VALUE_LENGTH,
  defaultGridState,
} from './services/constants';

import classes from './TripsTab.module.scss';
import Loader from '../../../kiosk/components/Loader';
import { commonTripSelector } from '../../../common/CommonTrip/selectors';
import { cleanError, getNewTripRequest } from '../../../common/CommonTrip/actions';
import { useModal } from '../../../shared/hooks';

type Props = {
  lolomode: number;
};

export const TripsTabContainer: React.FC<Props> = ({ lolomode }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const role = useSelector((state: RootState) => state.authModuleReducer.authReducer.role);
  const trips = useSelector((state: RootState) => state.orderingModuleReducer.tripsTabReducer.trips);
  const error = useSelector((state: RootState) => state.orderingModuleReducer.tripsTabReducer.error);
  const loading = useSelector((state: RootState) => state.orderingModuleReducer.tripsTabReducer.loading);
  const { branchTitle } = useSelector(logoutSelector);
  const { errorTrip } = useSelector(commonTripSelector);

  const operatingProcedure = useMemo(() => {
    if (role?.branches && role.branches.length !== 0) {
      return role.branches.find((branch) => branch?.id === JSON.parse(localStorage.getItem('branchId') || 'null')?.id)
        ?.operatingProcedure;
    }

    return null;
  }, [role]);

  const [filterInputValue, setFilterInputValue] = useState('');
  const filterInputRef = useRef<HTMLInputElement>(null);

  const { isShow: isShowErr, openModal: openErrToast, closeModal: closeErrToast } = useModal();

  const gridState = useGridState(trips, defaultGridState);

  const awaitingDepartureLength = useMemo(() => {
    if (!trips.length) return 0;

    const awaitingTrips = trips.filter((trip) => trip.statusId === AWAITING_REGISTER_STATUS_ID);

    return awaitingTrips.length;
  }, [trips]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    gridState.setFirstPage();
    setFilterInputValue(e.target.value);
  };

  const handleClearIconClick = () => {
    setFilterInputValue('');
    if (filterInputRef.current) {
      filterInputRef.current.focus();
    }
  };

  const addRowColorByStatus = (rowData: DefaultRecordType) => {
    const { statusId, id } = rowData;

    return {
      onDoubleClick: () => {
        history.push(`/ordering/trip/${id}`);
      },
      style: {
        backgroundColor: tripStatusBackgroundColor.ordering[operatingProcedure ?? ''][statusId as TripStatusIds],
      },
    };
  };

  const handleCreateTripBtnClick = (statusId: number) => {
    dispatch(getNewTripRequest(statusId));
  };

  const [filterColumnsSelected, setFilterColumnsSelected] = useState<string[]>([]);
  useEffect(() => {
    dispatch(lolomode === 1 ? getTripsRequest() : getUnloadingTripsRequest());
    gridState.setFirstPage();
    setFilterColumnsSelected([]);
  }, [lolomode]);

  useEffect(() => {
    setTimeout(() => dispatch(lolomode === 1 ? getTripsRequest() : getUnloadingTripsRequest()), 500);
  }, [branchTitle]);

  useEffect(() => {
    const updateData = setInterval(
      () => dispatch(lolomode === 1 ? getTripsRequest() : getUnloadingTripsRequest()),
      50000,
    );

    if (error) clearInterval(updateData);
    return function cleanup() {
      clearInterval(updateData);
    };
  }, [lolomode]);

  const handleFilterColumnsChange = (column: string, isChecked: boolean) => {
    if (isChecked && !filterColumnsSelected.includes(column)) {
      setFilterColumnsSelected([...filterColumnsSelected, column]);
    } else if (!isChecked && filterColumnsSelected.includes(column)) {
      setFilterColumnsSelected(filterColumnsSelected.filter((x) => x !== column));
    }
  };

  useEffect(() => {
    if (filterInputValue.length < MIN_FILTER_VALUE_LENGTH) {
      gridState.setFilter({
        logic: LogicTypes.and,
        filters: [],
      });
    } else {
      const filterColumns =
        filterColumnsSelected && filterColumnsSelected.length > 0
          ? filterColumnsSelected
          : Object.keys(GetTripColumnsNames(lolomode === 1));
      const newFilter = {
        logic: LogicTypes.or,
        filters: filterColumns.map(
          (name): Filter => ({
            operator: FilterOperators.contains,
            value: filterInputValue.trim(),
            field: name,
          }),
        ),
      } as GridFilter;
      gridState.setFilter(newFilter);
    }
  }, [filterInputValue, filterColumnsSelected]);

  useEffect(() => {
    if (errorTrip) {
      return openErrToast();
    }
    closeErrToast();
  }, [errorTrip]);

  const factoryColumnName = lolomode === 1 ? 'factoryNumber' : 'unloadingFactory';
  const warehouseColumnName = lolomode === 1 ? 'warehouseCode' : 'unloadingWarehouse';

  return (
    <React.Fragment>
      <Container fluid className={classes.filterContainer}>
        <Row>
          <Col className="d-flex align-items-center">
            {operatingProcedure && (operatingProcedure === 'Порядок 2' || operatingProcedure === 'Порядок 3') && (
              <>
                {lolomode === 1 && (
                  <Button variant="light" className="mr-2" onClick={() => handleCreateTripBtnClick(1)}>
                    <AddCircle className={classes.plusIcon} /> Завантаження
                  </Button>
                )}
                {lolomode === 2 && (
                  <Button variant="light" className="mr-2" onClick={() => handleCreateTripBtnClick(2)}>
                    <AddCircle className={classes.plusIcon} /> Розвантаження
                  </Button>
                )}
              </>
            )}
            <InputGroup className={classes.filter}>
              <Form.Control
                as="input"
                type="text"
                name="filter"
                onChange={handleFilterChange}
                value={filterInputValue}
                ref={filterInputRef}
              />
              <Search size={18} className={classes.searchIcon} />
              {!!filterInputValue && (
                <HighlightOff size={18} className={classes.clearFilterIcon} onClick={handleClearIconClick} />
              )}
            </InputGroup>
            <TripColumnsMultiSelect
              onFilterColumnsChange={handleFilterColumnsChange}
              columnNames={Object.entries(GetTripColumnsNames(lolomode === 1))}
              initSelectedColumns={filterColumnsSelected}
            />

            {!!trips.length && <span className="ml-2">Всього: {trips.length}</span>}
            {!!awaitingDepartureLength && <span className="ml-2">Очікує реєстрацію: {awaitingDepartureLength}</span>}
          </Col>
        </Row>
      </Container>
      {loading && <Loader />}
      {gridState.gridData ? (
        <DataGrid
          pageable
          data={gridState.gridData}
          sort={gridState.sort}
          total={gridState.total}
          skip={gridState.pagination?.skip}
          pageSize={gridState.pagination?.pageSize}
          onPageChange={gridState.handlePageChange}
          onSortChange={gridState.handleSortChange}
          onRow={addRowColorByStatus}
        >
          <DataGridColumn title="Рейс" dataIndex="id" key="id" width="5%" />
          <DataGridColumn
            title="Дата бронювання"
            dataIndex="bookingDate"
            key="bookingDate"
            dateFormat={clientDateFormat}
            width="12%"
            className={classes.bookingDate}
          />
          <DataGridColumn title="Статус" dataIndex="statusId" key="statusId" width="10%" render={StatusCell} sortable />
          <DataGridColumn hidden dataIndex="statusStr" key="statusStr" />
          <DataGridColumn title="Тип перевізника" dataIndex="сarrierTypeStr" key="driver" width="8%" />
          <DataGridColumn title="Водій" dataIndex="driver" key="driver" width="12%" />
          <DataGridColumn title="Телефон" dataIndex="driverPhone" key="driverPhone" width="9%" />
          <DataGridColumn
            title="В'їзд"
            dataIndex="arrivalDate"
            key="arrivalDate"
            dateFormat={clientDateFormat}
            width="12%"
            sortable
          />
          {/* <DataGridColumn title="Експедитор" dataIndex="forwarder" key="forwarder" width="12%" /> */}
          {/* <DataGridColumn title="Телефон" dataIndex="forwarderPhone" key="forwarderPhone" width="12%" /> */}
          {lolomode === 1 && <DataGridColumn title="Номер Наряда" dataIndex="orders" key="orders" width="10%" />}
          {lolomode === 1 && (
            <DataGridColumn
              title="Фактична вага,т (ТСД)"
              dataIndex="factWeightGrouped"
              key="factWeightGrouped"
              width="10%"
            />
          )}
          {lolomode === 2 && (
            <DataGridColumn title="Локація" dataIndex="unloadingLocation" key="unloadingLocation" width="10%" />
          )}
          <DataGridColumn title="Номер Заводу" dataIndex={factoryColumnName} key={factoryColumnName} width="5%" />{' '}
          <DataGridColumn title="Номер Складу" dataIndex={warehouseColumnName} key={warehouseColumnName} width="5%" />
          <DataGridColumn
            title="Кількість позицій у наряді"
            dataIndex="orderItemsCount"
            key="orderItemsCount"
            width="5%"
          />
        </DataGrid>
      ) : null}
      <ErrorMsg
        isShow={!!error}
        error={error?.message || 'Помилка при отриманні данних'}
        closeHandler={() => dispatch(getTripsError(null))}
      />
      <ErrorMsg
        isShow={isShowErr}
        closeHandler={() => {
          closeErrToast();
          dispatch(cleanError());
        }}
        error={errorTrip?.data?.Errors[0]?.Message || 'Виникла помилка!'}
      />
    </React.Fragment>
  );
};
